import { MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
// Materials
import { Collapse, Divider, Grid } from "@mui/material";
import { GridCellProps, GridColDef, GridValidRowModel, useGridApiRef } from "@mui/x-data-grid-premium";
// Helpers
import { getFormatUTCDate } from "@/Common/helpers/Dates";
import { applyFormatter } from "@/Washouts/helpers/tables";
import { getCellClassNameFn } from "@/Common/helpers/Tables";
import { errorLogger, getError } from "@/Common/helpers/logger";
import { and, nullishResolver, ternaryResolver } from "@/Common/helpers/sonar";
// Components
import AddCellIssues from "@/Layouts/components/Tables/DataSmartTable/additional/AddCellIssues";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { getBasicColumnWithTooltip, getUniformWidth } from "@/Layouts/components/Tables/DataSmartTable/helpers";
// Hooks
import useAggregationExtended from "@/Layouts/components/Tables/DataSmartTable/aggregation/Aggregation";
import {
	externalColumn,
	fundedColumn,
	getSalesPerformanceRowsWithSearchValues,
	resolveProductLineClamp,
} from "@DataPages/components/SalesOperations/Tabs/SalesPerformance/hooks/useSalesPerformanceTableConfiguration";
// Types
import { GridStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { FooterModelsToShow, IProductBase, IProductsBase, Product, SalesPerformanceTableProps } from "@/Washouts/types/sales";
import { StringMap } from "@/Common/interfaces/types";
import { DEFAULT_COLLAPSED_SIZE, HEADER_CLASS_NAME, ID_GRID_COLDEF } from "@/Washouts/constants";
import { Issue } from "@/Common/types/tables";
// Consts
import { COLUMN_RESIZE_FACTOR, ONE_HUNDRED_TWENTY } from "@/Constants/tables";
import FullCollapseContainer from "@/Layouts/components/Basics/FullCollapseContainer";
import { DATE_FORMAT } from "@/Constants/date";

const REFERENCE = "Performance";

const productsBase = (): IProductsBase => {
	return {
		front: {
			identifier: "frontProducts",
			max: 0,
			key: -1,
		},
		back: {
			identifier: "backProducts",
			max: 0,
			key: -1,
		},
	};
};

const processProducts = (props: SalesPerformanceTableProps, product: IProductBase) => {
	props.data?.forEach((element, key) => {
		if (element[product.identifier].length > product.max) {
			product.max = element[product.identifier].length;
			product.key = key;
		}
	});
};

const totalGrossProfitColumnDefinition = (issues: Issue[]): Partial<GridColDef> => {
	return {
		type: "number",
		minWidth: 105,
		flex: 1,
		align: "right",
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
		renderCell: (params) => (
			<TypographyCell render="absolute" reference={REFERENCE} params={params} issues={issues} hints={{ isFunded: false }} />
		),
		renderHeader: (params) => <TypographyHeader params={params} left />,
	};
};

const resolveProductsLists = (products: IProductsBase, useFrontProducts: boolean): IProductBase[] => {
	const result: IProductBase[] = [];
	if (useFrontProducts) {
		result.push(products.front);
	}
	result.push(products.back);
	return result;
};

const buildColumns = (items: Product[] = [], key = "", issues: Issue[] = []) => {
	return items.map((element: Product) => {
		const colDef: GridColDef = {
			field: `${key}_${element.product}`,
			headerName: element.product,
			type: "number",
			minWidth: 110,
			flex: 1,
			align: "right",
			filterable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { isFunded: false, isMoney: true }, {}),
			renderCell: (params) => (
				<TypographyCell
					render="absolute"
					reference={REFERENCE}
					params={params}
					issues={issues}
					hints={{ isFunded: false, isMoney: true }}
				/>
			),
			valueGetter: (_value: number, row: GridValidRowModel) => {
				const product = row?.[key]?.filter((item: Product) => item.product === element.product);
				return product?.[0]?.value || 0;
			},
			renderHeader: (params) => <TypographyHeader params={params} left lineClamp={resolveProductLineClamp(element.product, 60)} />,
		};
		return colDef;
	});
};

function getInitialColumns(issues: Issue[], departmentalView = false): GridColDef[] {
	const columns: GridColDef[] = [
		ID_GRID_COLDEF,
		fundedColumn,
		externalColumn,
		{
			field: "saleDate",
			headerName: "Date",
			minWidth: 80,
			flex: 0.25,
			type: "date",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateDashShortYear),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "stockNumber",
			headerName: "Stock",
			minWidth: 75,
			flex: 0.5,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "dealNumber",
			headerName: "Ref. #",
			minWidth: 80,
			flex: 0.5,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "saleType",
			headerName: "Sale Type",
			minWidth: 75,
			flex: 0.25,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "dealType",
			headerName: "Deal Type",
			minWidth: 80,
			flex: 0.25,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
	];
	if (departmentalView) {
		columns.push({
			field: "salesEmployeeName",
			headerName: "Sales Employee",
			headerClassName: HEADER_CLASS_NAME,
			...getUniformWidth(ONE_HUNDRED_TWENTY, COLUMN_RESIZE_FACTOR, true),
			cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
			renderCell: (params) => {
				let value = params.row.salesEmployeeName || params.row.salesEmployeeId;
				if (params.row.salesEmployeeId === "9999") {
					value = "HOUSE";
				}
				return getBasicColumnWithTooltip(value, params, params?.colDef?.width ?? ONE_HUNDRED_TWENTY * COLUMN_RESIZE_FACTOR);
			},
			renderHeader: (params) => <TypographyHeader params={params} />,
			align: "left",
		});
		columns.push({
			field: "financeEmployeeName",
			headerName: "Finance Employee",
			headerClassName: HEADER_CLASS_NAME,
			...getUniformWidth(ONE_HUNDRED_TWENTY, COLUMN_RESIZE_FACTOR, true),
			cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
			renderCell: (params) => {
				let value = params.row.financeEmployeeName || params.row.financeEmployeeId;
				if (params.row.financeEmployeeId === "9999") {
					value = "HOUSE";
				}
				return getBasicColumnWithTooltip(value, params, params?.colDef?.width ?? ONE_HUNDRED_TWENTY * COLUMN_RESIZE_FACTOR);
			},
			renderHeader: (params) => <TypographyHeader params={params} />,
			align: "left",
		});
	}

	columns.push({
		field: "salesManagerName",
		headerName: "Sales Manager",
		minWidth: 80,
		flex: 0.25,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: getCellClassNameFn(issues, {}, {}),
		renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
		renderHeader: (params) => <TypographyHeader params={params} />,
	});

	return columns;
}

function getInitialCommissionsColumns(issues: Issue[]): GridColDef[] {
	return [
		{
			field: "units",
			headerName: "Units",
			type: "number",
			minWidth: 65,
			flex: 0.1,
			align: "right",
			headerClassName: `${HEADER_CLASS_NAME}`,
			cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
			valueFormatter: (params) => applyFormatter(params, "account"),
			renderCell: (params) => (
				<TypographyCell render="account" reference={REFERENCE} params={params} issues={issues} hints={{ isFunded: false }} />
			),
			renderHeader: (params) => <TypographyHeader params={params} right />,
		},
		{
			field: "frontCommission",
			headerName: "Front Commission",
			type: "number",
			minWidth: 110,
			flex: 1,
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
			renderCell: (params) => (
				<TypographyCell render="absolute" reference={REFERENCE} params={params} issues={issues} hints={{ isFunded: false }} />
			),
			renderHeader: (params) => <TypographyHeader params={params} left />,
		},
		{
			field: "backCommission",
			headerName: "Back Commission",
			type: "number",
			minWidth: 110,
			flex: 1,
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
			renderCell: (params) => (
				<TypographyCell render="absolute" reference={REFERENCE} params={params} issues={issues} hints={{ isFunded: false }} />
			),
			renderHeader: (params) => <TypographyHeader params={params} left />,
		},
		{
			field: "grossCommission",
			headerName: "Gross Commission",
			type: "number",
			minWidth: 110,
			flex: 1,
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
			renderCell: (params) => (
				<TypographyCell render="absolute" reference={REFERENCE} params={params} issues={issues} hints={{ isFunded: false }} />
			),
			renderHeader: (params) => <TypographyHeader params={params} left />,
		},
	];
}

function getAggregationToUse(aggregation?: string) {
	return aggregation ?? "selectedSum";
}

function getEmptyTitle(title?: string) {
	return title ?? "Performance";
}

function BottomDivider({ collapsed, ...props }: Readonly<SalesPerformanceTableProps & { collapsed: boolean }>) {
	return (
		<Collapse in={props.divider} collapsedSize={0} timeout={100}>
			{!props.useFullCollapseContainer ? (
				<Divider
					className={clsx({
						"!mx-[24px] !border-b !border-[#CCC] !pt-[5px] !mt-[10px]": !props.sxSectionDivider && !collapsed,
						"!mx-[24px] !border-b !border-[#CCC] !pt-0": !props.sxSectionDivider && collapsed,
					})}
					sx={props.sxSectionDivider}
				></Divider>
			) : (
				<Divider
					className={clsx({
						"!mx-[24px] !border-b !border-[#CCC]": !props.sxSectionDivider,
					})}
					sx={props.sxSectionDivider}
				></Divider>
			)}
		</Collapse>
	);
}

function SalesTable(props: Readonly<SalesPerformanceTableProps>) {
	const emptyTitle = getEmptyTitle(props?.emptyTitle);
	const columnVisibility = props.columnVisibility ?? [];

	const apiRef = useGridApiRef();
	const [totalColumns, setTotalColumns] = useState<GridColDef[]>([]);
	const [footerModels, setFooterModels] = useState<FooterModelsToShow>(null);
	const [showColumns, setShowColumns] = useState(false);
	const [columnsToExpand, setColumnsToExpand] = useState<string[]>([]);
	const [collapsed, setCollapsed] = useState(true);

	// Issues support attributes
	const [openAddIssues, setOpenAddIssues] = useState(false);
	const [anchorAddIssues, setAnchorAddIssues] = useState<(HTMLDivElement & EventTarget) | null>(null);
	const [issues, setIssues] = useState<Issue[]>([]);
	const activeIssue = useRef<Issue>(null);

	const handleClickCell: MouseEventHandler<HTMLDivElement> = (event) => {
		activeIssue.current = {
			rowReference: event.currentTarget?.parentElement?.dataset.id, // _id
			columnReference: event.currentTarget?.dataset.field, // field
		};
		setAnchorAddIssues(event.currentTarget);
		setOpenAddIssues(true);
	};

	const updateCurrentIssue = (color: string, currentIssues: Issue[]) => {
		let isPresent = false;
		for (const issue of currentIssues) {
			if (
				issue &&
				issue.rowReference === activeIssue?.current?.rowReference &&
				issue.columnReference === activeIssue?.current?.columnReference
			) {
				issue.color = color;
				isPresent = true;
				break;
			}
		}

		if (!isPresent) {
			currentIssues.push({
				tableReference: props?.issuesReference,
				rowReference: activeIssue?.current?.rowReference ?? "",
				columnReference: activeIssue?.current?.columnReference ?? "",
				color,
			});
		}
		return currentIssues;
	};

	const handleChangeIssues = (color: string | null) => {
		let currentIssues = [...issues];
		if (color) {
			currentIssues = updateCurrentIssue(color, currentIssues);
		} else {
			currentIssues?.forEach((issue, key) => {
				if (
					issue?.rowReference === activeIssue?.current?.rowReference &&
					issue?.columnReference === activeIssue?.current?.columnReference
				) {
					currentIssues[key] = null;
				}
			});
			currentIssues = currentIssues.filter((issue) => issue);
		}

		setIssues(currentIssues);
		props?.onIssuesUpdatedAction?.(currentIssues, props?.issuesReference);
	};

	// Visibility of the columns should be controlled by state, the 'hide' prop will be deprecated in the future.
	const [columnVisibilityModel, setColumnVisibilityModel] = useState<StringMap<boolean>>({
		_id: false,
	});

	const getCustomValue = (row: StringMap<Product[]>, field: string) => {
		const fieldName = field?.split("_");
		if (fieldName.length > 1) {
			const [rowName, productName] = fieldName;
			return row[rowName].find((item: Product) => item.product === productName)?.value ?? 0;
		}
		return 0;
	};

	const checkConditions = (row: { funded: boolean }) => {
		return row.funded || false;
	};

	const aggregations = useAggregationExtended({
		selectedSum: {
			onlySelected: props?.totalsTypeTitle?.includes("Funded") ?? false,
			checkConditions,
			getCustomValue,
		},
	});

	useMemo(() => {
		let columns = getInitialColumns(issues, props?.departmentalView);
		try {
			const products = productsBase();
			[...resolveProductsLists(products, Boolean(props?.useFrontProducts))].forEach((product) => {
				processProducts(props, product);
			});

			let columnVisibilityIndex = -1;

			// Front Products
			if (products?.front?.key !== -1) {
				const frontProducts = buildColumns(
					props.data?.[products?.front?.key].frontProducts,
					"frontProducts",
					nullishResolver(issues, [])
				);
				frontProducts.forEach((frontProduct) => {
					columnVisibilityIndex = ternaryResolver(
						Boolean(columnVisibility),
						columnVisibility?.findIndex((item) => item.columnName === frontProduct.field),
						-1
					);
					if (and(columnVisibilityIndex !== -1, columnVisibility[columnVisibilityIndex]?.visibility !== "NOT_DISPLAYED")) {
						columns.push(frontProduct);
					}
				});
			}

			// Front Gross Profit
			columnVisibilityIndex = ternaryResolver(
				Boolean(columnVisibility),
				columnVisibility?.findIndex((item) => item.columnName === "frontGrossProfit"),
				-1
			);
			if (and(columnVisibilityIndex !== -1, columnVisibility[columnVisibilityIndex]?.visibility !== "NOT_DISPLAYED")) {
				columns.push({
					field: "frontGrossProfit",
					headerName: "Front Gross",
					...totalGrossProfitColumnDefinition(issues),
				});
			}

			// Back Products
			if (products?.back?.key !== -1) {
				const backProducts = buildColumns(
					props.data?.[products?.back?.key].backProducts,
					"backProducts",
					nullishResolver(issues, [])
				);
				backProducts.forEach((backProduct) => {
					columnVisibilityIndex = ternaryResolver(
						Boolean(columnVisibility),
						columnVisibility?.findIndex((item) => item.columnName === backProduct.field),
						-1
					);
					if (and(columnVisibilityIndex !== -1, columnVisibility[columnVisibilityIndex]?.visibility !== "NOT_DISPLAYED")) {
						columns.push(backProduct);
					}
				});
			}

			// Back Gross Profit
			columnVisibilityIndex = ternaryResolver(
				Boolean(columnVisibility),
				columnVisibility?.findIndex((item) => item.columnName === "backGrossProfit"),
				-1
			);
			if (and(columnVisibilityIndex !== -1, columnVisibility[columnVisibilityIndex]?.visibility !== "NOT_DISPLAYED")) {
				columns.push({
					field: "backGrossProfit",
					headerName: "Back Gross",
					...totalGrossProfitColumnDefinition(issues),
				});
			}

			// Append commissions columns
			columns = [...columns, ...getInitialCommissionsColumns(issues)];
		} catch (error) {
			errorLogger(getError(error));
		}

		setTotalColumns(columns);
	}, [props.data, issues, props?.departmentalView]);

	const clearFilters = useCallback(() => {
		// Clear filter on purpose
		try {
			const filter = apiRef.current?.state?.filter;
			if (filter) {
				const cleanFilter = {
					filterModel: { items: [], linkOperator: "and", quickFilterLogicOperator: "and", quickFilterValues: [] },
					visibleRows: null,
					visibleRowsLookup: {},
				};

				apiRef.current.setState((prevState) => {
					return {
						...prevState,
						filter: { ...filter, ...cleanFilter },
					} as GridStatePremium;
				});
			}
		} catch (error) {
			errorLogger(getError(error));
		}
	}, [apiRef]);

	useEffect(() => {
		const visibilityModel: StringMap<boolean> = {
			_id: false,
		};
		const defaultColumnsToExpand: string[] = [];
		const footerModelsToShow: FooterModelsToShow = {
			units: getAggregationToUse(props?.aggregationToUse),
			frontGrossProfit: getAggregationToUse(props?.aggregationToUse),
			backGrossProfit: getAggregationToUse(props?.aggregationToUse),
			frontCommission: getAggregationToUse(props?.aggregationToUse),
			backCommission: getAggregationToUse(props?.aggregationToUse),
			grossCommission: getAggregationToUse(props?.aggregationToUse),
		};

		const conditionalColumnPush = (args: { canPush: boolean; columnName: string }) => {
			// set the proper footer model even if the column is not visible
			footerModelsToShow[args.columnName] = getAggregationToUse(props?.aggregationToUse);
			if (args.canPush) {
				visibilityModel[args.columnName] = false;
				defaultColumnsToExpand.push(args.columnName);
			}
		};

		try {
			const products = productsBase();
			const productsToLoop = resolveProductsLists(products, Boolean(props?.useFrontProducts));
			[...productsToLoop].forEach((product) => {
				processProducts(props, product);
			});

			let columnName = "";
			let columnVisibilityIndex = -1;
			const getVisibilityIndex = (column: string) =>
				ternaryResolver(
					Boolean(columnVisibility),
					columnVisibility?.findIndex((item) => item.columnName === column),
					-1
				);

			// front & back products
			[
				{
					identifier: "front",
					key: products.front.key,
				},
				{
					identifier: "back",
					key: products.back.key,
				},
			].forEach((product) => {
				if (product.key !== -1) {
					props.data?.[product.key][`${product.identifier}Products`].forEach((element: Product) => {
						columnName = `${product.identifier}Products_${element.product}`;
						columnVisibilityIndex = getVisibilityIndex(columnName);
						conditionalColumnPush({
							canPush: and(
								columnVisibilityIndex !== -1,
								columnVisibility[columnVisibilityIndex]?.visibility === "EXPANDED_VISIBLE"
							),
							columnName,
						});
					});
				}
			});

			// Front Gross Profit && Back Gross Profit
			const grossColumnNames = ["frontGrossProfit", "backGrossProfit"];
			grossColumnNames.forEach((column) => {
				columnName = column;
				columnVisibilityIndex = ternaryResolver(
					Boolean(columnVisibility),
					columnVisibility?.findIndex((item) => item.columnName === columnName),
					-1
				);
				conditionalColumnPush({
					canPush: and(columnVisibilityIndex !== -1, columnVisibility[columnVisibilityIndex]?.visibility === "EXPANDED_VISIBLE"),
					columnName,
				});
			});
		} catch (error) {
			errorLogger(getError(error));
		}

		clearFilters();

		setIssues(props.issues ?? []);
		setColumnVisibilityModel(visibilityModel);
		setFooterModels(footerModelsToShow);
		setColumnsToExpand(defaultColumnsToExpand);
		setCollapsed(Boolean(props?.collapsed));

		return () => {
			setShowColumns(false);
			setTotalColumns([]);
			setFooterModels(null);
			setColumnsToExpand([]);
			setColumnVisibilityModel({
				_id: false,
			});
			setCollapsed(true);

			// Issues controls
			setIssues([]);
			activeIssue.current = null;
			setOpenAddIssues(false);
			setAnchorAddIssues(null);
		};
	}, [props, apiRef, clearFilters]);

	if (!totalColumns || !footerModels) {
		return null;
	}

	function getCell(): Partial<GridCellProps> | undefined {
		if (props?.issues) {
			return { onClick: handleClickCell };
		}
	}

	const table = (
		<DataSmartTable
			{...props.tableProps}
			sx={{ width: "1400px" }}
			apiRef={apiRef}
			columns={totalColumns}
			rows={props.data ?? []}
			hideFooter
			disableColumnMenu
			disableRowSelectionOnClick
			disableMultipleRowSelection
			checkboxSelection={false}
			columnVisibilityModel={columnVisibilityModel}
			initialState={{
				aggregation: {
					model: footerModels,
				},
				sorting: {
					sortModel: [{ field: "saleDate", sort: "asc" }],
				},
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
				noResultsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					titleExpandible: props.useFullCollapseContainer
						? undefined
						: {
								collapsed,
								hide: false,
								collapsible: true,
								title: emptyTitle,
								titleClasses: "page-title",
								sxContainer: { padding: "20px 32px" },
								onClick: (value: boolean) => setCollapsed(value),
						  },
					filter: {
						hide: false,
					},
					search: {
						data: getSalesPerformanceRowsWithSearchValues(props?.data ?? []),
						hide: false,
						disabled: false,
					},
					expand: {
						setColumnVisibilityModel,
						hide: false,
						expanded: showColumns,
						columnsCollapsible: columnsToExpand,
						onExpandAction: (expanded: boolean) => {
							setShowColumns(expanded);
						},
					},
				},
				noRowsOverlay: {
					title: `No ${emptyTitle} Data`,
				},
				cell: getCell(),
			}}
			customProps={{
				tableClassName: {
					"[&_.MuiDataGrid-main]:mt-0 [&_.MuiDataGrid-main]:mx-[30px]": true,
					"[&_.toolbar-actions]:absolute [&_.toolbar-actions]:top-[15px]": !props.useFullCollapseContainer,
					"[&_.toolbar-actions]:absolute [&_.toolbar-actions]:top-[-4px]": Boolean(props.useFullCollapseContainer),
				},
				customAggregationsFn: { ...aggregations },
			}}
		/>
	);

	const cellIssues = (
		<AddCellIssues
			open={openAddIssues}
			anchorEl={anchorAddIssues}
			onCloseAction={() => {
				setOpenAddIssues(false);
				setAnchorAddIssues(null);
			}}
			onChangeAction={handleChangeIssues}
		/>
	);

	return (
		<>
			{props.useFullCollapseContainer ? (
				<FullCollapseContainer title={emptyTitle} arrow={true}>
					<div className="mb-[30px]">
						{props.beforeTableComponent}
						{table}
						{cellIssues}
					</div>
				</FullCollapseContainer>
			) : (
				<Collapse in={!collapsed} collapsedSize={DEFAULT_COLLAPSED_SIZE} timeout={100}>
					<div className="mb-[15px]">
						{table}
						{cellIssues}
					</div>
				</Collapse>
			)}

			<BottomDivider {...props} collapsed={collapsed} />
		</>
	);
}

export default function SalesPerformanceTable(props: Readonly<SalesPerformanceTableProps>) {
	if (props.loading) {
		return (
			<>
				<Grid className="py-[20px] px-[32px]">
					<TableLoader smallTitle toolbar />
				</Grid>
				{props.divider && (
					<Divider
						className={clsx({
							"!mx-[24px] !border-b !border-[#CCC]": !props.sxSectionDivider,
						})}
						sx={props.sxSectionDivider}
					></Divider>
				)}
			</>
		);
	}
	if (props?.departmentalView && !props?.data?.length) {
		return <></>;
	}
	return <>{props.dataPresent && <SalesTable {...props} />}</>;
}
